import { ReactNode, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import { getUsername } from 'Features/Auth/auth'

type BrandBarProps = {
  /** The product word. With `mark`, the only thing that differs between portals. */
  product?: string
  /** The glyph in the tile. The Planner uses a grid; Modelling a network hub. */
  mark?: ReactNode
  /** Optional mono context string — the Scheduler uses it for a schedule id. */
  context?: string
  /** Rendered immediately after the mark, before the spacer. */
  trailing?: ReactNode
}

/**
 * The application bar — the Planner Portal's `BrandBar` (44px, brand teal),
 * with the `mark` slot the shell spec gives each product. Replaces the old
 * `Supplyvue-ui/Header`, whose 58x150px bitmap logo cannot sit in a 44px bar.
 *
 * Every measurement here is a literal px or a token, never the `sx` spacing
 * shorthand: theme.spacing(1) is 4px in this theme.
 */
export const BrandBar = ({
  product = 'Modelling',
  mark = <HubOutlinedIcon sx={{ fontSize: 15, color: 'var(--surface-0)' }} />,
  context,
  trailing,
}: BrandBarProps) => {
  const navigate = useNavigate()
  const tenantName = localStorage.getItem('tenantName') ?? ''
  const [userName, setUserName] = useState('')

  useEffect(() => {
    getUsername().then((name) => setUserName((name as string) ?? ''))
  }, [])

  const tenantSwitchEnabled =
    parseInt(localStorage.getItem('tenantsCount') as string) > 1

  return (
    <Box
      component="header"
      sx={{
        height: 'var(--brand-bar-h)',
        flexShrink: 0,
        background: 'var(--brand-teal)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        px: '14px',
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => tenantName && navigate('/')}
        aria-label={`${product} home`}
        sx={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          p: 0,
          '&:focus-visible': { outlineColor: 'var(--surface-0)' },
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: 'var(--radius-md)',
            background: 'var(--brand-blue)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {mark}
        </Box>
        <Box
          sx={{
            color: 'var(--surface-0)',
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '-0.01em',
            lineHeight: 1,
          }}
        >
          {product}
        </Box>
        <Box
          sx={{
            color: 'rgba(255,255,255,0.55)',
            fontWeight: 500,
            fontSize: 12,
            lineHeight: 1,
          }}
        >
          SupplyVue
        </Box>
      </Box>

      {context && (
        <>
          <Box sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>/</Box>
          <Box
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              color: 'rgba(255,255,255,0.85)',
            }}
          >
            {context}
          </Box>
        </>
      )}

      {trailing}

      <Box sx={{ flex: 1 }} />

      {tenantName && (
        <Box sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
          {tenantName}
        </Box>
      )}
      {userName && (
        <Box sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
          {userName}
        </Box>
      )}
      {tenantSwitchEnabled && (
        <Box
          component="button"
          type="button"
          title="Switch tenant"
          onClick={() => navigate('/select-tenant')}
          sx={{
            border: '1px solid rgba(255,255,255,0.3)',
            background: 'transparent',
            color: 'var(--surface-0)',
            borderRadius: 'var(--radius-md)',
            px: '8px',
            py: '2px',
            fontFamily: 'var(--font-sans)',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': { background: 'rgba(255,255,255,0.1)' },
            '&:focus-visible': { outlineColor: 'var(--surface-0)' },
          }}
        >
          Switch
        </Box>
      )}
    </Box>
  )
}
